'use client';

import { usePathname } from 'next/navigation';
import { useState, useEffect, useId, useRef } from 'react';
import Link from 'next/link';
import { Logo } from './Logo';
import { MobileMenu } from './MobileMenu';
import { NavLink } from './NavLink';
import type { NavLinkData, SiteNavigationData } from '@/sanity/types';
import { HEADER_BOOK_CALL_CTA, PRIMARY_CTA, localizeAnchorHref } from '@/lib/constants';
import { cn } from '@/lib/utils';

/**
 * A nav item whose panel opens on hover or focus, with the open state exposed
 * to assistive tech.
 *
 * Both desktop navs used to be pure CSS — `group-hover:` / `group-focus-within:`
 * toggling opacity — so nothing in the DOM ever said whether a panel was open.
 * A screen-reader user was told "Shopify Experts, link" and nothing more: no
 * indication that eight more links had just appeared (finding uiux-scfet2).
 *
 * The visuals stay on CSS. React state here exists only to mirror what the CSS
 * is already doing into `aria-expanded`, so this cannot change how the menu
 * looks or behaves for pointer users — it only stops the DOM lying about it.
 *
 * Deliberately no `aria-haspopup`. That attribute's `true` value means `menu`,
 * which promises the arrow-key semantics of a real menu widget; this panel is
 * a plain list of links, and claiming `menu` would advertise keyboard
 * behaviour that is not implemented. `aria-expanded` plus `aria-controls` is
 * the APG disclosure-navigation pattern and is what this actually is.
 */
function NavDisclosure({
  link,
  triggerClassName,
  triggerActiveClassName,
  panelClassName,
  panelListClassName,
  childClassName,
  analyticsCta,
  analyticsChildCta,
  analyticsLocation,
}: {
  link: NavLinkData;
  triggerClassName: string;
  triggerActiveClassName: string;
  panelClassName: string;
  panelListClassName: string;
  childClassName: string;
  analyticsCta: string;
  analyticsChildCta: string;
  analyticsLocation: string;
}) {
  const children = link.children ?? [];
  const [open, setOpen] = useState(false);
  const panelId = useId();

  return (
    <li
      className="group relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      // Only collapse when focus leaves the item entirely — tabbing from the
      // trigger into the panel's own links must not report it as closed.
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setOpen(false);
        }
      }}
    >
      <NavLink
        href={link.href}
        aria-expanded={open}
        aria-controls={panelId}
        data-analytics-cta={analyticsCta}
        data-analytics-location={analyticsLocation}
        data-analytics-label={link.label}
        className={triggerClassName}
        activeClassName={triggerActiveClassName}
      >
        {link.label}
        <ChevronDown className="transition-transform duration-200 group-hover:rotate-180" />
      </NavLink>

      <div id={panelId} className={panelClassName}>
        <ul className={panelListClassName}>
          {children.map((child) => (
            <li key={child.href}>
              <NavLink
                href={child.href}
                data-analytics-cta={analyticsChildCta}
                data-analytics-location={analyticsLocation}
                data-analytics-label={child.label}
                className={childClassName}
                activeClassName="text-accent hover:text-accent"
              >
                {child.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
}

function ChevronDown({ className }: { className?: string }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('shrink-0', className)}
    >
      <path d="M3 4.5L6 7.5L9 4.5" />
    </svg>
  );
}

function TopBar({
  visible,
  navigation,
}: {
  visible: boolean;
  navigation: SiteNavigationData;
}) {
  const pathname = usePathname();
  const hasLinks = navigation.topNav.length > 0;
  const hasCta = navigation.cta !== null;
  if (!hasLinks && !navigation.topNavContact && !hasCta) return null;

  return (
    <div
      className={cn(
        'hidden bg-accent transition-all duration-300 lg:block',
        visible ? 'max-h-[60px] opacity-100' : 'max-h-0 overflow-hidden opacity-0',
      )}
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-3 md:px-10 xl:max-w-[1600px] 2xl:max-w-[1920px]">
        {hasLinks ? (
          <nav aria-label="Secondary navigation">
            <ul className="flex flex-wrap items-center gap-x-10 gap-y-1">
              {navigation.topNav.map((link) => {
                const hasChildren = (link.children ?? []).length > 0;

                if (hasChildren) {
                  return (
                    <NavDisclosure
                      key={link.href}
                      link={link}
                      analyticsCta="header_top_nav"
                      analyticsChildCta="header_top_nav_child"
                      analyticsLocation="header_top_bar"
                      triggerClassName="flex items-center gap-1 text-[14px] text-background transition-colors hover:text-background/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background 2xl:text-[16px]"
                      /*
                       * The current-page link is dimmed, and at opacity-60 the
                       * dimming took it under AA: #121212 at 60% over the
                       * accent bar composites to #2a5e14, 3.82:1 against the
                       * 4.5:1 that 14px text needs. opacity-75 reads 5.58:1
                       * and is still clearly dimmer than its siblings, so the
                       * "you are here" cue survives without the failure.
                       */
                      triggerActiveClassName="opacity-75 hover:opacity-75"
                      panelClassName="pointer-events-none absolute left-0 top-full z-50 pt-3 opacity-0 transition-opacity duration-150 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100"
                      panelListClassName="flex min-w-[220px] flex-col gap-1 rounded-2xl border border-foreground/10 bg-background p-2 shadow-2xl"
                      childClassName="block whitespace-nowrap rounded-xl px-4 py-2.5 text-[15px] text-foreground/90 transition-colors hover:bg-foreground/5 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent 2xl:text-[17px]"
                    />
                  );
                }

                return (
                  <li key={link.href}>
                    <NavLink
                      href={link.href}
                      data-analytics-cta="header_top_nav"
                      data-analytics-location="header_top_bar"
                      data-analytics-label={link.label}
                      className="flex items-center gap-1 text-[14px] text-background transition-colors hover:text-background/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background 2xl:text-[16px]"
                      activeClassName="opacity-75 hover:opacity-75"
                    >
                      {link.label}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>
        ) : (
          <span />
        )}

        <div className="flex items-center gap-6">
          {navigation.topNavContact ? (
            <span className="text-[14px] text-background 2xl:text-[16px]">
              {navigation.topNavContact}
            </span>
          ) : null}
          {navigation.cta ? (
            <Link
              href={localizeAnchorHref(navigation.cta.href, pathname)}
              data-analytics-cta="header_top_cta"
              data-analytics-location="header_top_bar"
              data-analytics-label={navigation.cta.label}
              className="flex items-center gap-1.5 text-[16px] uppercase tracking-[-0.02em] text-background underline underline-offset-2 transition-colors hover:text-background/70 2xl:text-[20px]"
            >
              {navigation.cta.label}
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Persistent desktop lead CTA. Replaces the old decorative (non-interactive)
 * hamburger lines — the header previously had no conversion path once the
 * accent top bar scrolled away.
 */
function HeaderCta() {
  const pathname = usePathname();

  return (
    <div className="hidden items-center gap-5 lg:flex">
      {/* Direct calendar path beside the quote pill (finding
          conversion-8nqxmk) — the global CalendlyPopup opens this in an
          embedded modal, so booking is one click from any page. */}
      <a
        href={HEADER_BOOK_CALL_CTA.href}
        data-analytics-cta="header_book_call"
        data-analytics-location="header_main_bar"
        className="whitespace-nowrap text-[14px] font-light text-foreground/80 underline underline-offset-4 transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent 2xl:text-[16px]"
      >
        {HEADER_BOOK_CALL_CTA.label}
      </a>
      <Link
        href={localizeAnchorHref(PRIMARY_CTA.href, pathname)}
        data-analytics-cta="header_quote_cta"
        data-analytics-location="header_main_bar"
        className="flex items-center gap-2 rounded-full bg-accent px-6 py-3 text-[14px] font-normal uppercase tracking-[-0.02em] text-background transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background 2xl:text-[16px]"
      >
        {PRIMARY_CTA.label}
      </Link>
    </div>
  );
}

interface HeaderClientProps {
  navigation: SiteNavigationData;
}

/**
 * Sticky site header body: reads navigation from Sanity (via props) and
 * handles scroll-driven show/hide + top-bar visibility.
 */
export function HeaderClient({ navigation }: HeaderClientProps) {
  const [isAtTop, setIsAtTop] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);

  useEffect(() => {
    function onScroll() {
      const y = window.scrollY;

      setIsAtTop(y < 10);

      if (y < 100) {
        setIsVisible(true);
      } else if (y > lastScrollY.current + 5) {
        setIsVisible(false);
      } else if (y < lastScrollY.current - 5) {
        setIsVisible(true);
      }

      lastScrollY.current = y;
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      // The consent modal may set `inert` here before hydration (useInertBackground).
      suppressHydrationWarning
      className={cn(
        // Desktop (lg+) stays visible on scroll so the header CTA is a
        // persistent conversion path; mobile keeps the auto-hide behavior
        // and gets the sticky bottom CTA instead.
        'fixed left-0 top-0 z-50 w-screen max-w-[100vw] transition-transform duration-300',
        isVisible ? 'translate-y-0' : '-translate-y-full lg:translate-y-0',
      )}
    >
      <TopBar visible={isAtTop} navigation={navigation} />

      <div className="bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-4 md:px-10 md:py-5 xl:max-w-[1600px] 2xl:max-w-[1920px]">
          <Logo className="text-[28px] md:text-[34px] xl:text-[42px]" />

          {navigation.mainNav.length > 0 ? (
            <nav className="hidden lg:block" aria-label="Main navigation">
              <ul className="flex items-center gap-6 2xl:gap-10">
                {navigation.mainNav.map((link) => {
                  const hasChildren = (link.children ?? []).length > 0;

                  if (hasChildren) {
                    return (
                      <NavDisclosure
                        key={link.href}
                        link={link}
                        analyticsCta="header_main_nav"
                        analyticsChildCta="header_main_nav_child"
                        analyticsLocation="header_main_bar"
                        triggerClassName="flex items-center gap-1 text-[16px] text-foreground transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent 2xl:text-[20px]"
                        triggerActiveClassName="text-accent hover:text-accent"
                        panelClassName="pointer-events-none absolute left-0 top-full z-50 pt-4 opacity-0 transition-opacity duration-150 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100"
                        panelListClassName="flex min-w-[240px] flex-col gap-1 rounded-2xl border border-border/10 bg-background/95 p-2 shadow-2xl backdrop-blur-md"
                        childClassName="block whitespace-nowrap rounded-xl px-4 py-2.5 text-[15px] text-foreground/90 transition-colors hover:bg-foreground/5 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent 2xl:text-[17px]"
                      />
                    );
                  }

                  return (
                    <li key={link.href}>
                      <NavLink
                        href={link.href}
                        data-analytics-cta="header_main_nav"
                        data-analytics-location="header_main_bar"
                        data-analytics-label={link.label}
                        className="flex items-center gap-1 text-[16px] text-foreground transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent 2xl:text-[20px]"
                        activeClassName="text-accent hover:text-accent"
                      >
                        {link.label}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </nav>
          ) : null}

          <HeaderCta />

          <MobileMenu navigation={navigation} />
        </div>
      </div>
    </header>
  );
}
